import { useBloc } from "@blac/react";
import Chart from "molecule/chart/Chart";
import React, { FC, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ChartEmptyState from "./ChartEmptyState";
import DataHubCubit from "./DataHubCubit";
import { LoincCodingCode } from "src/constants/fhir";
import { globalEvents } from "src/constants/globalEvents";
import ChartLoadingState from "./ChartLoadingState";
import ChartComingSoon from "./ChartComingSoon";
import { ChartColors } from "atom/chart/ChartBaseConfig";
import ChartCustomizedRectangle, {
  Series
} from "molecule/chart/ChartCustomizedRectangle";

const DataHubChart: FC = () => {
  const navigate = useNavigate();
  const [
    { activeLoincCode, chartData, chartLabel, activeTimeRange },
    {
      tickFormatterAxisX,
      formatLabel,
      formatValue,
      availableCharts,
      clearApiCache,
      setActiveObservations,
      formatValueRemoveZeroDecimals
    }
  ] = useBloc(DataHubCubit);
  const isAvailable = activeLoincCode
    ? availableCharts.includes(activeLoincCode)
    : false;

  const handleReload = useCallback(() => {
    clearApiCache(activeLoincCode);
    void setActiveObservations({
      loincCode: activeLoincCode,
      forceRefresh: true
    });
  }, [activeLoincCode, setActiveObservations]);

  const type = new URLSearchParams(window.location.search).get("type");

  useEffect(() => {
    if (type) {
      void setActiveObservations({
        loincCode: type as LoincCodingCode,
        forceRefresh: true
      });
    }
  }, [type, setActiveObservations]);

  useEffect(() => {
    if (activeLoincCode) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set("type", activeLoincCode);
      navigate(`?${searchParams.toString()}`, { replace: true });
    }
  }, [activeLoincCode, navigate]);

  useEffect(() => {
    const handleDataSubmitted = (event: Event) => {
      const customEvent = event as CustomEvent<{ loincCode?: LoincCodingCode }>;
      const submittedLoincCode = customEvent.detail.loincCode;

      // If a loinc code was provided, switch to that chart
      if (submittedLoincCode) {
        void setActiveObservations({
          loincCode: submittedLoincCode,
          forceRefresh: true
        });
      } else {
        handleReload();
      }
    };

    document.addEventListener(globalEvents.DATA_SUBMITTED, handleDataSubmitted);

    return () => {
      document.removeEventListener(
        globalEvents.DATA_SUBMITTED,
        handleDataSubmitted
      );
    };
  }, [handleReload, setActiveObservations]);

  const defaultXAxis: React.ComponentProps<typeof Chart>["xAxis"] = {
    type: "number",
    scale: "time",
    tickFormatter: (time: number) => tickFormatterAxisX(time),
    ticks: chartLabel?.ticks,
    domain: chartLabel?.domain,
    dataKey: "time"
  };

  // loading state for chart
  if (isAvailable && typeof chartData === "undefined") {
    return <ChartLoadingState />;
  }

  if (!isAvailable) {
    return <ChartComingSoon />;
  }

  if (!chartData || chartData.length === 0) {
    return <ChartEmptyState />;
  }

  return (
    <>
      {activeLoincCode === LoincCodingCode.waistCircumference && (
        <Chart
          //** Waist Circumference Specific */
          lines={[
            {
              color: ChartColors.Violet,
              dataKey: "primaryValue"
            }
          ]}
          xAxis={defaultXAxis}
          yAxis={{
            tickFormatter: (value) =>
              // eslint-disable-next-line @typescript-eslint/no-unsafe-return
              typeof value === "number"
                ? formatValueRemoveZeroDecimals(value)
                : value,
            domain: [20, 60],
            padding: { bottom: 10 }
          }}
          //** Default props */
          type="line"
          chartData={chartData}
          tooltip={{
            formatLabel,
            formatValue
          }}
        />
      )}

      {activeLoincCode === LoincCodingCode.weight && (
        <Chart
          //** Weight Specific */
          lines={[
            {
              color: ChartColors.Pink,
              dataKey: "primaryValue"
            }
          ]}
          xAxis={defaultXAxis}
          yAxis={{
            tickFormatter: (value) =>
              // eslint-disable-next-line @typescript-eslint/no-unsafe-return
              typeof value === "number" ? Math.round(value) : value,
            domain: [140, 180],
            padding: { bottom: 10 }
          }}
          //** Default props */
          type="line"
          chartData={chartData}
          tooltip={{
            formatLabel,
            formatValue
          }}
        />
      )}

      {activeLoincCode === LoincCodingCode.hba1c && (
        <Chart
          //** A1c Specific */
          lines={[
            {
              color: ChartColors.Orange,
              dataKey: "primaryValue"
            }
          ]}
          xAxis={defaultXAxis}
          yAxis={{ ticks: [5.7, 6.5], domain: [4.8, 7] }}
          referenceLines={[
            { y: 5.7, stroke: ChartColors.Green },
            { y: 6.5, stroke: ChartColors.Orange }
          ]}
          //** Default props */
          type="line"
          chartData={chartData}
          tooltip={{
            formatLabel,
            formatValue
          }}
        />
      )}

      {activeLoincCode === LoincCodingCode.stepsInDay && (
        <Chart
          //** Steps Specific */
          bars={[
            {
              color: ChartColors.VioletLight,
              dataKey: "primaryValue"
            }
          ]}
          xAxis={defaultXAxis}
          yAxis={{
            ticks: [5000, 10000, 15000],
            domain: [0, 20000],
            tickFormatter: (value: number) => {
              return `${(value / 1000).toFixed(1).replace(/\.0$/, "")}K`;
            }
          }}
          //** Default props */
          type="bar"
          chartData={chartData}
          tooltip={{
            formatLabel,
            formatValue
          }}
        />
      )}

      {activeLoincCode === LoincCodingCode.bloodPressure && (
        <Chart
          //** BP Specific */
          lines={[
            {
              color: ChartColors.Blue,
              dataKey: "primaryValue"
            },
            {
              color: ChartColors.BlueLight,
              dataKey: "secondaryValue"
            }
          ]}
          xAxis={defaultXAxis}
          yAxis={{
            ticks: [50, 100, 150, 200],
            domain: [0, 250]
          }}
          //** Default props */
          type="line"
          chartData={chartData}
          tooltip={{
            formatLabel,
            formatValue
          }}
        />
      )}

      {activeLoincCode === LoincCodingCode.bloodGlucoseCapillary && (
        <Chart
          //** Blood Glucose Specific */
          type="line"
          xAxis={defaultXAxis}
          yAxis={{
            ticks: [70, 180],
            domain: [40, 250]
          }}
          referenceLines={[
            { y: 70, stroke: ChartColors.Green },
            { y: 180, stroke: ChartColors.Green }
          ]}
          customized={(props) => (
            <ChartCustomizedRectangle
              formattedGraphicalItems={
                props.formattedGraphicalItems as Series[]
              }
              timeRange={activeTimeRange?.domain.days === 1 ? "1d" : undefined}
            />
          )}
          lines={[
            {
              color: ChartColors.Blue,
              dataKey: "primaryValue",
              props: {
                stroke: "none",
                dot: (props: {
                  cx: number;
                  cy: number;
                  payload: { primaryValue: number };
                }) => {
                  const value = props.payload.primaryValue;
                  let color;

                  if (value < 70) {
                    color = ChartColors.Red;
                  } else if (value > 180) {
                    color = ChartColors.Red;
                  } else {
                    color = ChartColors.Green;
                  }

                  return (
                    <circle
                      cx={props.cx}
                      cy={props.cy}
                      r={6}
                      fill={color}
                      stroke="white"
                      strokeWidth={2}
                    />
                  );
                },
                activeDot: (props: unknown) => {
                  const { cx, cy, payload } = props as {
                    cx: number;
                    cy: number;
                    payload: { primaryValue: number };
                  };

                  const value = payload.primaryValue;
                  let color;

                  if (value < 70) {
                    color = ChartColors.Red;
                  } else if (value > 180) {
                    color = ChartColors.Red;
                  } else {
                    color = ChartColors.Green;
                  }

                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={8}
                      fill={color}
                      stroke="white"
                      strokeWidth={3}
                    />
                  );
                }
              }
            }
          ]}
          chartData={chartData}
          tooltip={{
            formatLabel,
            formatValue
          }}
        />
      )}
    </>
  );
};
export default DataHubChart;
